import { REHYDRATE } from 'redux-persist';
import update from 'immutability-helper';
import get from 'lodash/get';
import find from 'lodash/find';
import {
  ANY_FIELD_UPDATE,
  FORMFIELD_UPDATE,
  ANY_FIELD_REMOVE,
  CLONE_OBJECT,
  VALIDATE_BEGIN,
  VALIDATE_SUCCESS,
  VALIDATE_ERROR,
  addNestedFields,
  removeNestedFields,
  updatedObject,
  getObject,
} from 'actions/formActions';
import { LOGOUT } from 'actions/loginStateActions';
import { INSPECTION_JOB_FETCH_SUCCESS } from 'views/Inspection/Job/types';
import { replaceOrInsertItem } from 'util/array';

import InspectionTypes from './types';
import INITIAL_STATE from './state';

export default function inspectionJobDetailWorkspaceReducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case REHYDRATE:
      return state;

    case ANY_FIELD_UPDATE: {
      if (action.storeName !== 'inspectionJobDetailWorkspace') return state;
      return {
        ...state,
        data: updatedObject(state.data, action.accessPath, action.newVal),
        isChanged: true,
      };
    }

    case ANY_FIELD_REMOVE:
      if (action.storeName !== 'inspectionJobDetailWorkspace') return state;

      return { ...state, data: removeNestedFields(state.data, action.accessPath) };

    case FORMFIELD_UPDATE:
      if (action.storeName !== 'inspectionJobDetailWorkspace') return state;

      return Object.assign({}, state, {
        data: { ...state.data, [action.fieldName]: action.newVal },
        isChanged: true,
      });

    case VALIDATE_BEGIN:
      if (action.storeName !== 'inspectionJobDetailWorkspace') return state;
      return { ...state, errors: [], status: 'validating' };

    case VALIDATE_SUCCESS:
      if (action.storeName !== 'inspectionJobDetailWorkspace') return state;
      return { ...state, errors: INITIAL_STATE.errors, status: 'validated' };

    case VALIDATE_ERROR:
      if (action.storeName !== 'inspectionJobDetailWorkspace') return state;
      return { ...state, errors: action.errors, status: 'failed' };

    case CLONE_OBJECT: {
      if (action.storeName !== 'inspectionJobDetailWorkspace') return state;

      const cloneObject = getObject(state.data, action.accessPath, action.idx);

      return { ...state, data: addNestedFields(state.data, action.accessPath, cloneObject) };
    }

    case InspectionTypes.INSPECTION_JOB_DETAIL_INIT:
      return { ...INITIAL_STATE, status: 'initialized' };

    case INSPECTION_JOB_FETCH_SUCCESS: {
      const { inspectionFactory } = action.data;
      return update(state, {
        data: {
          invoice: {
            factory: {
              $set: {
                name:
                  (inspectionFactory && inspectionFactory.name) ||
                  (inspectionFactory && inspectionFactory.nameChi) ||
                  null,
                address:
                  (inspectionFactory && inspectionFactory.address) ||
                  (inspectionFactory && inspectionFactory.addressChi) ||
                  null,
                country: (inspectionFactory && inspectionFactory.country) || null,
                city: (inspectionFactory && inspectionFactory.city) || null,
                contactPerson: (inspectionFactory && inspectionFactory.contactPerson) || null,
                email: (inspectionFactory && inspectionFactory.contactPersonEmail) || null,
                phone: (inspectionFactory && inspectionFactory.contactPersonPhone) || null,
              },
            },
          },
        },
      });
    }

    case InspectionTypes.INSPECTION_JOB_DETAIL_INVOICE_INIT:
      return update(state, {
        status: { $set: INITIAL_STATE.status },
        errors: { $set: INITIAL_STATE.errors },
        activeStep: { $set: INITIAL_STATE.activeStep },
        data: {
          invoice: {
            $merge: {
              ...action.quoteId,
              addBuyerInfo: '0',
              addSupplierInfo: '0',
              isBuyerSame: false,
              isSupplierSame: false,
              company: {
                name: action.company.company_name || action.company.companyNameChi || null,
                address:
                  `${action.company.address} ${action.company.address2}` ||
                  `${action.company.addressChi} ${action.company.addressChi2}` ||
                  null,
                country: action.company.country || action.company.countryChi || null,
                city: action.company.city || action.company.companyStateProvince || null,
                contactPerson: action.company.contactPerson || null,
                email: action.company.email || null,
                phone: action.company.contactPersonPhone || null,
              },
              buyer: {},
              supplier: {},
              invoice: {
                name: action.company.company_name || action.company.companyNameChi || null,
                address:
                  `${action.company.address} ${action.company.address2}` ||
                  `${action.company.addressChi} ${action.company.addressChi2}` ||
                  null,
                country: action.company.country || action.company.countryChi || null,
                city: action.company.city || action.company.companyStateProvince || null,
                contactPerson: action.company.contactPerson || null,
                email: action.company.email || null,
                phone: action.company.contactPersonPhone || null,
              },
              invoiceType: 'ICWCompanyInvoice',
              chinaBrFile: [],
            },
          },
        },
      });

    case InspectionTypes.INSPECTION_JOB_INVOICE_TOGGLE: {
      return update(state, {
        data: {
          invoice: action.checked
            ? {
                $merge: {
                  [action.context]: {
                    name: state.data.invoice.company.name,
                    address: state.data.invoice.company.address,
                    country: state.data.invoice.company.country,
                    city: state.data.invoice.company.city,
                    contactPerson: state.data.invoice.company.contactPerson,
                    email: state.data.invoice.company.email,
                    phone: state.data.invoice.company.phone,
                  },
                },
              }
            : { $unset: [[action.context]] },
        },
      });
    }

    case InspectionTypes.INSPECTION_JOB_INVOICE_PREV_STEP: {
      return Object.assign({}, state, {
        activeStep: state.activeStep - 1,
        status: 'editing',
      });
    }

    case InspectionTypes.INSPECTION_JOB_INVOICE_NEXT_STEP: {
      return Object.assign({}, state, {
        activeStep: state.activeStep + 1,
        status: 'editing',
      });
    }

    case InspectionTypes.INSPECTION_REPORT_FETCH_BEGIN: {
      return { ...state, status: 'fetching' };
    }

    case InspectionTypes.INSPECTION_REPORT_FETCH_SUCCESS: {
      return update(state, {
        data: {
          form: { $merge: action.data.form },
          report: { $merge: action.data.report },
          reportIDs: { $merge: action.data.reportIDs },
        },
        status: { $set: 'editing' },
      });
    }

    case InspectionTypes.INSPECTION_REPORT_FETCH_FAIL:
      return { ...state, status: 'fail', reason: action.reason };

    case InspectionTypes.INSPECTION_JOB_INVOICE_SUBMIT_BEGIN:
      return { ...state, status: 'submitting' };

    case InspectionTypes.INSPECTION_JOB_INVOICE_SUBMIT_SUCCESS:
      return { ...state, status: 'uploading' };

    case InspectionTypes.INSPECTION_JOB_INVOICE_UPLOAD_SUCCESS:
      return { ...state, status: 'completed' };

    case InspectionTypes.INSPECTION_JOB_INVOICE_SUBMIT_FAIL:
      return { ...state, status: 'failed', failReason: action.reason };

    // new Invoice
    case InspectionTypes.INSPECTION_JOB_INVOICE_GET_LIST.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            ...state.data.invoiceList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_INVOICE_GET_LIST.SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            ...state.data.invoiceList,
            loading: false,
            data: action.payload,
            error: null,
          },
          inspectionInvoices: action.payload,
        },
      };
    case InspectionTypes.INSPECTION_JOB_INVOICE_GET_LIST.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            loading: false,
            data: {},
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_INVOICE_CREATE.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            ...state.data.invoiceList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_INVOICE_CREATE.SUCCESS: {
      // const newInvoiceList = action.payload;
      // const newList = [...newInvoiceList, ...get(state, 'data.invoiceList.data.list', [])];
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            loading: false,
            data: action.payload, // { ...state.data.invoiceList.data, list: newList },
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_JOB_INVOICE_CREATE.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            loading: false,
            data: {},
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_INVOICE_UPDATE.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            ...state.data.invoiceList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_INVOICE_UPDATE.SUCCESS: {
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            loading: false,
            data: action.payload,
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_JOB_INVOICE_UPDATE.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            loading: false,
            data: {},
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_INVOICE_DELETE.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            ...state.data.invoiceList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_INVOICE_DELETE.SUCCESS: {
      const removedInvoiceId = Number(action.payload);
      const newList = get(state, 'data.invoiceList.data.list', []).filter(
        (item) => item.id !== removedInvoiceId
      );
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            loading: false,
            data: { ...state.data.invoiceList.data, list: newList },
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_JOB_INVOICE_DELETE.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceList: {
            ...state.data.invoiceList,
            loading: false,
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_INVOICE_DOWNLOAD_LIST.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceDownloadedList: {
            loading: true,
            data: {},
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_INVOICE_DOWNLOAD_LIST.SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceDownloadedList: {
            loading: false,
            data: action.payload,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_INVOICE_DOWNLOAD_LIST.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceDownloadedList: {
            loading: false,
            data: {},
            error: action.payload,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_INVOICE_DOWNLOAD_LIST.RESET:
      return {
        ...state,
        data: {
          ...state.data,
          invoiceDownloadedList: INITIAL_STATE.data.invoiceDownloadedList,
        },
      };

    // new Report
    case InspectionTypes.INSPECTION_JOB_REPORT_GET_LIST.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_GET_LIST.SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: false,
            data: action.payload,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_GET_LIST.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            loading: false,
            data: {},
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_REPORT_GET_DETAIL.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          reportDetail: {
            loading: true,
            data: {},
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_GET_DETAIL.SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          reportDetail: {
            loading: false,
            data: action.payload,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_GET_DETAIL.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          reportDetail: {
            loading: false,
            data: {},
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_REPORT_CREATE.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_CREATE.SUCCESS: {
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            loading: false,
            data: { ...state.data.reportList.data, ...action.payload },
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_JOB_REPORT_CREATE.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: false,
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_REPORT_DUPLICATE.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_DUPLICATE.SUCCESS: {
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            loading: false,
            data: { ...state.data.reportList.data, ...action.payload },
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_JOB_REPORT_DUPLICATE.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: false,
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_REPORT_UPDATE.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_UPDATE.SUCCESS: {
      const list = replaceOrInsertItem(
        state.data.reportList.data,
        { id: action.payload.id },
        action.payload
      );
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            loading: false,
            data: list,
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_JOB_REPORT_UPDATE.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: false,
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_REPORT_DELETE.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_DELETE.SUCCESS: {
      const removedReportId = Number(action.payload);
      const list = get(state, 'data.reportList.data', []).filter(
        (item) => item.id !== removedReportId
      );
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            loading: false,
            data: list,
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_JOB_REPORT_DELETE.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: false,
            error: null,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_REPORT_DOWNLOAD.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          reportDownloadedList: {
            loading: true,
            data: {},
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_DOWNLOAD.SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          reportDownloadedList: {
            loading: false,
            data: action.payload,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_DOWNLOAD.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          reportDownloadedList: {
            loading: false,
            data: {},
            error: action.payload,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_DOWNLOAD.RESET:
      return {
        ...state,
        data: {
          ...state.data,
          reportDownloadedList: INITIAL_STATE.data.reportDownloadedList,
        },
      };

    case InspectionTypes.INSPECTION_JOB_REPORT_PO_ITEM_UPDATE.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_PO_ITEM_UPDATE.SUCCESS: {
      const list = replaceOrInsertItem(
        state.data.reportList.data,
        { id: action.payload.id },
        action.payload
      );
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            loading: false,
            data: list,
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_JOB_REPORT_PO_ITEM_UPDATE.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: false,
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_REPORT_PO_ITEM_DELETE.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_PO_ITEM_DELETE.SUCCESS: {
      const report = find(state.data.reportList.data, { id: action.payload.reportId });
      const list = replaceOrInsertItem(
        state.data.reportList.data,
        { id: action.payload.reportId },
        {
          ...report,
          ...action.payload.inspectionPOItems.filter((item) => item.id !== action.payload.poItemId),
        }
      );
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            loading: false,
            data: list,
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_JOB_REPORT_PO_ITEM_DELETE.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: false,
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_REPORT_PO_ITEM_DEFECT_ITEM_DELETE.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_JOB_REPORT_PO_ITEM_DEFECT_ITEM_DELETE.SUCCESS: {
      const report = find(state.data.reportList.data, { id: action.payload.reportId });

      const poItem = find(report.inspectionPOItems, { id: action.payload.poItemId });

      const updatedPoItems = replaceOrInsertItem(
        report.inspectionPOItems,
        { id: action.payload.poItemId },
        {
          ...poItem,
          inspectionDefectItems: poItem.inspectionDefectItems.filter(
            (defect) => defect.id !== action.payload.defectItemId
          ),
        }
      );

      const list = replaceOrInsertItem(
        state.data.reportList.data,
        { id: action.payload.reportId },
        { ...report, inspectionPOItems: updatedPoItems }
      );
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            loading: false,
            data: list,
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_JOB_REPORT_PO_ITEM_DEFECT_ITEM_DELETE.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          reportList: {
            ...state.data.reportList,
            loading: false,
            error: action.payload,
          },
        },
      };

    //-------------------------------------------------
    // INSPECTION_GET_ATTACHMENTS
    case InspectionTypes.INSPECTION_GET_ATTACHMENTS.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            ...state.data.attachments,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_GET_ATTACHMENTS.SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            loading: false,
            data: action.payload,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_GET_ATTACHMENTS.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            loading: false,
            data: {},
            error: action.payload,
          },
        },
      };

    //-------------------------------------------------
    // INSPECTION_DOWNLOAD_ATTACHMENTS
    case InspectionTypes.INSPECTION_DOWNLOAD_ATTACHMENTS.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          attachmentsDownloaded: {
            ...state.data.attachmentsDownloaded,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_DOWNLOAD_ATTACHMENTS.SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          attachmentsDownloaded: {
            loading: false,
            data: action.payload,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_DOWNLOAD_ATTACHMENTS.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          attachmentsDownloaded: {
            loading: false,
            data: {},
            error: action.payload,
          },
        },
      };
    case InspectionTypes.INSPECTION_DOWNLOAD_ATTACHMENTS.RESET:
      return {
        ...state,
        data: {
          ...state.data,
          attachmentsDownloaded: { loading: false, data: {}, error: null },
        },
      };

    //-------------------------------------------------
    // INSPECTION_ADD_ATTACHMENT
    case InspectionTypes.INSPECTION_ADD_ATTACHMENT.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            ...state.data.attachments,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_ADD_ATTACHMENT.SUCCESS: {
      const newAttachmentData = { ...get(state, 'data.attachments.data', {}), ...action.payload };

      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            loading: false,
            data: { ...newAttachmentData },
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_ADD_ATTACHMENT.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            ...state.data.attachments,
            loading: false,
            error: action.payload,
          },
        },
      };

    //-------------------------------------------------
    // INSPECTION_DELETE_ATTACHMENT
    case InspectionTypes.INSPECTION_DELETE_ATTACHMENT.LOADING:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            ...state.data.attachments,
            loading: true,
            error: null,
          },
        },
      };
    case InspectionTypes.INSPECTION_DELETE_ATTACHMENT.SUCCESS: {
      const removedAttachmentId = Number(action.payload);
      const attachmentData = get(state, 'data.attachments.data', {});
      const newList = get(attachmentData, 'list', []).filter(
        (item) => item.id !== removedAttachmentId
      );
      const totalNo =
        get(attachmentData, 'totalNo', 0) > 1 ? get(attachmentData, 'totalNo') - 1 : 0;

      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            loading: false,
            data: { ...attachmentData, list: newList, totalNo },
            error: null,
          },
        },
      };
    }
    case InspectionTypes.INSPECTION_DELETE_ATTACHMENT.ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            ...state.data.attachments,
            loading: false,
            error: action.payload,
          },
        },
      };

    case InspectionTypes.INSPECTION_JOB_DETAIL_RESET:
    case LOGOUT:
      return INITIAL_STATE;

    default:
      return state;
  }
}
